import React from 'react';
import { FONT_MONO, IMG } from './landingData';

/**
 * The design's studio-launch overlay ("BUILDING YOUR SITE" / "OPENING YOUR
 * PROJECT"): three floating browser/phone mockups, the wordmark, the copy with
 * pulsing dots and a progress bar, on the desk background. `kind="video"` renders
 * the video studio design's variant (phone story + timeline, "Creating Your Video").
 *
 * Lifecycle across the page load: the landing/account page calls `startBoot()`
 * on click (records the copy + timestamp in sessionStorage), shows this overlay,
 * and does the full navigation to /studio (needed for the COOP/COEP headers).
 * The studio reads the entry and keeps the SAME overlay up until both the design's
 * 2.2 s have passed since the click AND the project data is loaded, then fades it.
 */

export type BootCopy = 'BUILDING YOUR SITE' | 'OPENING YOUR PROJECT' | 'CREATING YOUR VIDEO' | 'OPENING STUDIO' | `OPENING ${string}`;
/** Which studio the overlay leads to: the website one (browser/phone mockups) or the video one (phone + timeline). */
export type BootKind = 'site' | 'video';

export const BOOT_KEY = 'hyvay:boot';
/** The design routes to the studio after 2.2 s; we keep the overlay at least that long. */
export const BOOT_MIN_MS = 2200;
/** The video studio design fades its overlay 1.5 s after arriving. */
export const BOOT_MIN_MS_VIDEO = 1500;
export const BOOT_FADE_MS = 380;

export function startBoot(copy: BootCopy, kind: BootKind = 'site'): void {
    try { sessionStorage.setItem(BOOT_KEY, JSON.stringify({ copy, kind, at: Date.now() })); } catch { /* ignore */ }
}

/** The pending launch for this studio kind; an entry meant for the other studio is ignored. */
export function readBoot(kind: BootKind = 'site'): { copy: BootCopy; at: number } | null {
    try {
        const raw = sessionStorage.getItem(BOOT_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw);
        const k: BootKind = v?.kind === 'video' ? 'video' : 'site';
        if (k === kind && typeof v?.copy === 'string' && /^(BUILDING|OPENING|CREATING) /.test(v.copy) && v.copy.length <= 120 && typeof v.at === 'number') {
            return { copy: v.copy as BootCopy, at: v.at };
        }
    } catch { /* ignore */ }
    return null;
}

export function clearBoot(): void {
    try { sessionStorage.removeItem(BOOT_KEY); } catch { /* ignore */ }
}

interface Props {
    copy: BootCopy;
    /** Plays the fade-out; unmount after BOOT_FADE_MS. */
    hiding?: boolean;
    dark?: boolean;
    kind?: BootKind;
}

const dotBg = (i: number): React.CSSProperties => ({ width: 4, height: 4, borderRadius: 999, background: 'var(--hv-brand-ink)', animation: `hvBootDot 1.1s ease-in-out ${i * 0.18}s infinite` });
const pill = (w: number, h: number, bg: string, radius: number | string = 999): React.CSSProperties => ({ width: w, height: h, borderRadius: radius, background: bg, display: 'block' });
const pillPct = (w: string, h: number, bg: string, radius: number | string = 999): React.CSSProperties => ({ width: w, height: h, borderRadius: radius, background: bg, display: 'block' });
const star = 'M12 0l2.4 9.6L24 12l-9.6 2.4L12 24l-2.4-9.6L0 12l9.6-2.4z';

/** Wordmark, the copy with pulsing dots, and the sliding loading bar: shared by both overlay variants. */
const BootBrand: React.FC<{ copy: string; dark?: boolean }> = ({ copy, dark }) => (
    <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--hv-ink)' }}>
            {dark ? (
                <img src={IMG.logoDark} alt="" style={{ height: 28, width: 28, display: 'block', objectFit: 'cover' }} />
            ) : (
                <img src={IMG.logoLight} alt="" style={{ height: 28, width: 28, display: 'block', objectFit: 'cover', mixBlendMode: 'multiply' }} />
            )}
            <span style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.01em' }}>HyVay</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>
            <span style={{ maxWidth: 'min(420px, 70vw)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{copy}</span>
            <span style={{ display: 'flex', flex: 'none', gap: 4 }}>
                <span style={dotBg(0)} /><span style={dotBg(1)} /><span style={dotBg(2)} />
            </span>
        </div>
        <div style={{ width: 148, height: 3, borderRadius: 999, overflow: 'hidden', background: 'var(--hv-line)' }}>
            <div style={{ width: '100%', height: '100%', borderRadius: 999, background: 'var(--hv-brand-ink)', animation: 'hvBootBar 1.15s cubic-bezier(0.5,0.05,0.4,0.95) infinite' }} />
        </div>
    </div>
);

export const BootOverlay: React.FC<Props> = ({ copy, hiding, dark, kind = 'site' }) => (kind === 'video' ? <VideoBootOverlay copy={copy} hiding={hiding} dark={dark} /> : (
    <div
        role="status"
        aria-live="polite"
        style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 34, background: 'var(--hv-desk)', animation: hiding ? `hvBootOut ${BOOT_FADE_MS}ms ease forwards` : 'none' }}
    >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.8, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 55% 45% at 50% 45%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 55% 45% at 50% 45%,black,transparent)' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', width: 620, height: 400, margin: '-230px 0 0 -310px', borderRadius: '50%', pointerEvents: 'none', background: 'radial-gradient(closest-side,color-mix(in srgb,var(--brand,#13422C) 16%,transparent),transparent)', filter: 'blur(30px)', animation: 'hvBootGlow 2.6s ease-in-out infinite' }} />

        <div style={{ position: 'relative', zIndex: 2, width: 520, height: 310, maxWidth: '92vw' }}>
            {/* browser card */}
            <div style={{ position: 'absolute', left: 8, top: 0, zIndex: 1, animation: 'hvBootCardA 3.4s cubic-bezier(0.4,0,0.35,1) 0s infinite' }}>
                <div style={{ width: 300, borderRadius: 14, overflow: 'hidden', background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 40px 64px -36px var(--hv-shadow)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 26, padding: '0 10px', background: 'var(--hv-card-2)', borderBottom: '1px solid var(--hv-line)' }}>
                        <span style={pill(6, 6, 'var(--hv-line-2)')} /><span style={pill(6, 6, 'var(--hv-line-2)')} /><span style={pill(6, 6, 'var(--hv-line-2)')} />
                        <span style={{ flex: 1, height: 11, marginLeft: 6, borderRadius: 999, background: 'var(--hv-page-2)' }} />
                    </div>
                    <div style={{ padding: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={pill(16, 16, 'var(--brand,#13422C)', 5)} />
                            <span style={pill(48, 7, 'var(--hv-line-2)')} />
                            <span style={{ flex: 1 }} />
                            <span style={pill(28, 7, 'var(--hv-line)')} /><span style={pill(28, 7, 'var(--hv-line)')} />
                            <span style={pill(40, 15, 'var(--hv-mint)')} />
                        </div>
                        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 7 }}>
                            <span style={pillPct('78%', 12, 'var(--hv-line-2)', 4)} />
                            <span style={pillPct('56%', 12, 'var(--hv-line-2)', 4)} />
                            <span style={pillPct('88%', 6, 'var(--hv-line)')} />
                            <span style={pillPct('70%', 6, 'var(--hv-line)')} />
                        </div>
                        <div style={{ marginTop: 13, display: 'flex', gap: 8 }}>
                            <span style={pill(68, 22, 'var(--brand,#13422C)')} />
                            <span style={{ width: 58, height: 22, borderRadius: 999, border: '1px solid var(--hv-line-2)', display: 'block' }} />
                        </div>
                        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                            {[0, 1, 2].map((i) => <div key={i} style={{ height: 50, borderRadius: 8, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }} />)}
                        </div>
                    </div>
                </div>
            </div>

            {/* phone card */}
            <div style={{ position: 'absolute', left: 296, top: 52, zIndex: 2, animation: 'hvBootCardC 3.4s cubic-bezier(0.4,0,0.35,1) 0.42s infinite' }}>
                <div style={{ width: 140, padding: 8, borderRadius: 22, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 40px 60px -36px var(--hv-shadow)' }}>
                    <div style={{ borderRadius: 16, overflow: 'hidden', background: 'var(--hv-page-2)', padding: '10px 9px' }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}><span style={pill(32, 4, 'var(--hv-line-2)')} /></div>
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={pillPct('66%', 9, 'var(--hv-line-2)', 3)} />
                            <span style={pillPct('90%', 5, 'var(--hv-line)')} />
                        </div>
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[['70%', '44%'], ['60%', '38%']].map(([a, b], i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                                    <span style={{ ...pill(20, 20, 'var(--hv-card-2)', 6), flex: 'none' }} />
                                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <span style={pillPct(a, 5, 'var(--hv-line-2)')} />
                                        <span style={pillPct(b, 5, 'var(--hv-line)')} />
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 10, height: 22, borderRadius: 999, background: 'var(--brand,#13422C)' }} />
                    </div>
                </div>
            </div>

            {/* live-url pill */}
            <div style={{ position: 'absolute', left: 44, bottom: 2, zIndex: 3, animation: 'hvBootCardB 3.4s cubic-bezier(0.4,0,0.35,1) 0.72s infinite' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, height: 38, padding: '0 15px', borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', border: '1px solid color-mix(in srgb,#2E9E64 26%,transparent)', boxShadow: '0 26px 40px -30px var(--hv-shadow)' }}>
                    <span style={pill(8, 8, '#2E9E64')} />
                    <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.06em' }}>hyvay.app/kopi-rembuk</span>
                </div>
            </div>

            <div style={{ position: 'absolute', right: 44, bottom: 34, animation: 'hvBob 5s ease-in-out infinite' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="var(--hv-clay)" aria-hidden="true"><path d={star} /></svg>
            </div>
            <div style={{ position: 'absolute', right: 8, top: 26, animation: 'hvBob 6.4s ease-in-out 0.7s infinite' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--hv-line-2)" aria-hidden="true"><path d={star} /></svg>
            </div>
        </div>

        <BootBrand copy={copy} dark={dark} />
    </div>
));

const WAVE = [6, 10, 14, 8, 12, 16, 9, 5, 11, 15, 7, 13, 10, 6, 14, 9, 12, 7, 15, 8, 11, 6, 13, 10, 8, 14, 7, 12, 9, 5];
const CLIPS: [number, string, number][] = [
    [70, 'var(--hv-mint)', 0],
    [96, 'color-mix(in srgb,var(--hv-clay,#D9825B) 30%,var(--hv-card))', 0.3],
    [54, 'var(--hv-page-2)', 0.6],
    [66, 'var(--hv-mint)', 0.9],
];

/** The video studio design's launch overlay: a phone story card and a timeline card with a moving playhead, over the same wordmark / copy / loading bar as the site one. */
const VideoBootOverlay: React.FC<{ copy: string; hiding?: boolean; dark?: boolean }> = ({ copy, hiding, dark }) => (
    <div
        role="status"
        aria-live="polite"
        style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 34, background: 'var(--hv-desk)', animation: hiding ? `hvBootOut ${BOOT_FADE_MS}ms ease forwards` : 'none' }}
    >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.8, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 55% 45% at 50% 45%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 55% 45% at 50% 45%,black,transparent)' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', width: 620, height: 400, margin: '-230px 0 0 -310px', borderRadius: '50%', pointerEvents: 'none', background: 'radial-gradient(closest-side,color-mix(in srgb,var(--brand,#13422C) 16%,transparent),transparent)', filter: 'blur(30px)', animation: 'hvBootGlow 2.6s ease-in-out infinite' }} />

        <div style={{ position: 'relative', zIndex: 2, width: 520, height: 310, maxWidth: '92vw' }}>
            {/* phone story card */}
            <div style={{ position: 'absolute', left: 60, top: 0, zIndex: 2, animation: 'hvBootCardA 3.4s cubic-bezier(0.4,0,0.35,1) 0s infinite' }}>
                <div style={{ width: 150, height: 236, padding: 7, borderRadius: 24, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 40px 64px -36px var(--hv-shadow)', boxSizing: 'border-box' }}>
                    <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 18, overflow: 'hidden', background: 'linear-gradient(160deg,var(--hv-sand,#E9DCC6),var(--hv-page-2))' }}>
                        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, display: 'flex', gap: 3, padding: '8px 9px' }}>
                            <span style={{ flex: 1, height: 2.5, borderRadius: 999, background: 'var(--hv-ink)', opacity: 0.8 }} />
                            <span style={{ flex: 1, height: 2.5, borderRadius: 999, background: 'color-mix(in srgb,var(--hv-ink) 25%,transparent)', overflow: 'hidden' }}>
                                <span style={{ display: 'block', height: '100%', width: '100%', background: 'var(--hv-ink)', opacity: 0.9, transformOrigin: 'left', animation: 'hvBootFill 2.4s linear infinite' }} />
                            </span>
                            <span style={{ flex: 1, height: 2.5, borderRadius: 999, background: 'var(--hv-ink)', opacity: 0.25 }} />
                        </div>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', width: 44, height: 44, margin: '-22px 0 0 -22px', borderRadius: 999, background: 'var(--hv-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px -10px var(--hv-shadow)', animation: 'hvBootGlow 2.6s ease-in-out infinite' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--brand,#13422C)" aria-hidden="true">
                                <path d="M8 6.6c0-1.2 1.3-1.9 2.3-1.3l8.4 5.4c.9.6.9 2 0 2.6l-8.4 5.4c-1 .6-2.3-.1-2.3-1.3z" stroke="var(--brand,#13422C)" strokeWidth="1.2" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div style={{ position: 'absolute', left: 10, right: 10, bottom: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
                            <span style={{ width: '76%', height: 8, borderRadius: 3, background: 'var(--hv-ink)', opacity: 0.75 }} />
                            <span style={{ width: '52%', height: 5, borderRadius: 999, background: 'var(--hv-ink)', opacity: 0.35 }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* timeline card */}
            <div style={{ position: 'absolute', left: 150, top: 150, zIndex: 3, animation: 'hvBootCardB 3.4s cubic-bezier(0.4,0,0.35,1) 0.5s infinite' }}>
                <div style={{ width: 320, maxWidth: 'calc(92vw - 150px)', padding: 10, borderRadius: 14, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 40px 60px -36px var(--hv-shadow)', boxSizing: 'border-box', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>00:12</span>
                        <span style={{ flex: 1, height: 1, background: 'var(--hv-line)' }} />
                        <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>00:30</span>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', gap: 4, height: 34 }}>
                            {CLIPS.map(([w, bg, d], i) => (
                                <div key={i} style={{ flex: 'none', width: w, height: '100%', borderRadius: 6, background: bg, border: '1px solid var(--hv-line)', animation: `hvBootClip 2.4s ease-in-out ${d}s infinite` }} />
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 16, marginTop: 5, padding: '0 2px' }}>
                            {WAVE.map((h, i) => <span key={i} style={{ flex: 1, height: h, borderRadius: 999, background: 'var(--hv-line-2)' }} />)}
                        </div>
                        <div style={{ position: 'absolute', top: -4, bottom: -2, left: 0, width: 2, borderRadius: 999, background: 'var(--hv-danger,#D93025)', animation: 'hvBootPlayhead 2.4s linear infinite' }}>
                            <span style={{ position: 'absolute', top: -3, left: -3, width: 8, height: 8, borderRadius: 999, background: 'var(--hv-danger,#D93025)' }} />
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ position: 'absolute', right: 30, top: 6, animation: 'hvBob 5s ease-in-out infinite' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="var(--hv-clay)" aria-hidden="true"><path d={star} /></svg>
            </div>
            <div style={{ position: 'absolute', left: 14, bottom: 40, animation: 'hvBob 6.4s ease-in-out 0.7s infinite' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--hv-line-2)" aria-hidden="true"><path d={star} /></svg>
            </div>
        </div>

        <BootBrand copy={copy} dark={dark} />
    </div>
);

/**
 * Overlay state for the page that RECEIVES the navigation (the studio): visible
 * until `ready` and the minimum duration since the click have both passed, then
 * a fade, then gone. Falls back to `fallback` (no minimum) for direct visits and
 * refreshes.
 */
export function useBootOverlay(
    ready: boolean,
    opts: { kind?: BootKind; fallback?: BootCopy; minMs?: number } = {},
): { copy: BootCopy; hiding: boolean } | null {
    const { kind = 'site', fallback = 'OPENING YOUR PROJECT', minMs = BOOT_MIN_MS } = opts;
    const [entry] = React.useState(() => readBoot(kind));
    const [phase, setPhase] = React.useState<'shown' | 'hiding' | 'gone'>('shown');
    React.useEffect(() => {
        if (!ready || phase !== 'shown') return;
        const startedAt = entry?.at ?? 0;
        const wait = entry ? Math.max(0, startedAt + minMs - Date.now()) : 0;
        const t1 = setTimeout(() => {
            setPhase('hiding');
            if (entry) clearBoot();
        }, wait);
        return () => clearTimeout(t1);
    }, [ready, phase, entry, minMs]);
    React.useEffect(() => {
        if (phase !== 'hiding') return;
        const t = setTimeout(() => setPhase('gone'), BOOT_FADE_MS);
        return () => clearTimeout(t);
    }, [phase]);
    if (phase === 'gone') return null;
    return { copy: entry?.copy ?? fallback, hiding: phase === 'hiding' };
}
